import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatNotificationStatus } from "./scheduleActivityLogUtils";

const PANEL_WIDTH = 268;
const PANEL_MAX_HEIGHT = 300;

function FriendlyValue({ label, value, changed, isOld }) {
  const text =
    label === "Email" || label === "SMS"
      ? formatNotificationStatus(label, value)
      : value || "—";

  return (
    <div
      className={`schedule-activity-log__ecomm-friendly${
        isOld ? " schedule-activity-log__ecomm-friendly--old" : ""
      }${changed && !isOld ? " schedule-activity-log__ecomm-friendly--new" : ""}`}
    >
      {text}
    </div>
  );
}

function ContentBlock({ block, hasOld }) {
  const showOld = hasOld && block.changed && block.oldValue;
  const showNew = block.newValue;
  if (!showOld && !showNew) return null;

  return (
    <div
      className={`gvisit-comm-suggestion__block${
        block.tone ? ` gvisit-comm-suggestion__block--${block.tone}` : ""
      } schedule-activity-log__ecomm-block`}
    >
      <div className="gvisit-comm-suggestion__block-head">
        <span className="gvisit-comm-suggestion__block-title">{block.label}</span>
        {block.changed ? (
          <span className="schedule-activity-log__ecomm-badge">Changed</span>
        ) : null}
      </div>
      {showOld ? (
        <FriendlyValue label={block.label} value={block.oldValue} changed={block.changed} isOld />
      ) : null}
      {showOld && showNew ? (
        <i className="fas fa-arrow-down schedule-activity-log__ecomm-arrow" aria-hidden />
      ) : null}
      {showNew ? <FriendlyValue label={block.label} value={block.newValue} changed={block.changed} /> : null}
    </div>
  );
}

function NotificationsSection({ emailBlock, smsBlock, hasOld }) {
  if (!emailBlock && !smsBlock) return null;
  const changed = Boolean(emailBlock?.changed || smsBlock?.changed);

  return (
    <div className="gvisit-comm-suggestion__block schedule-activity-log__ecomm-block schedule-activity-log__ecomm-block--channels">
      <div className="gvisit-comm-suggestion__block-head">
        <span className="gvisit-comm-suggestion__block-title">How participants are notified</span>
        {changed ? <span className="schedule-activity-log__ecomm-badge">Changed</span> : null}
      </div>
      {hasOld && changed ? (
        <div className="schedule-activity-log__ecomm-channel-group">
          <span className="schedule-activity-log__ecomm-channel-label">Before</span>
          {emailBlock ? (
            <FriendlyValue label="Email" value={emailBlock.oldValue} changed isOld />
          ) : null}
          {smsBlock ? <FriendlyValue label="SMS" value={smsBlock.oldValue} changed isOld /> : null}
        </div>
      ) : null}
      {hasOld && changed ? (
        <i className="fas fa-arrow-down schedule-activity-log__ecomm-arrow" aria-hidden />
      ) : null}
      <div className="schedule-activity-log__ecomm-channel-group">
        {hasOld && changed ? (
          <span className="schedule-activity-log__ecomm-channel-label">After</span>
        ) : null}
        {emailBlock ? <FriendlyValue label="Email" value={emailBlock.newValue} changed={emailBlock.changed} /> : null}
        {smsBlock ? <FriendlyValue label="SMS" value={smsBlock.newValue} changed={smsBlock.changed} /> : null}
      </div>
    </div>
  );
}

function buildPopoverSections(blocks = []) {
  const emailBlock = blocks.find((block) => block.label === "Email") || null;
  const smsBlock = blocks.find((block) => block.label === "SMS") || null;
  const contentBlocks = blocks.filter((block) => !["Email", "SMS"].includes(block.label));
  return { emailBlock, smsBlock, contentBlocks };
}

function computePanelStyle(anchorRect) {
  if (!anchorRect) return null;
  const margin = 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  let left = anchorRect.left;
  let top = anchorRect.bottom + 6;
  if (left + PANEL_WIDTH > viewportWidth - margin) left = anchorRect.right - PANEL_WIDTH;
  left = Math.max(margin, Math.min(left, viewportWidth - PANEL_WIDTH - margin));
  if (top + PANEL_MAX_HEIGHT > viewportHeight - margin) top = anchorRect.top - PANEL_MAX_HEIGHT - 6;
  top = Math.max(margin, Math.min(top, viewportHeight - PANEL_MAX_HEIGHT - margin));
  return {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    width: `${PANEL_WIDTH}px`,
    maxHeight: `${PANEL_MAX_HEIGHT}px`,
    zIndex: 12000,
  };
}

export default function ScheduleEcommAuditViewLink({ diff, variant = "sidebar" }) {
  const popoverId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const useClick = variant === "embedded";
  const sections = useMemo(() => buildPopoverSections(diff?.blocks || []), [diff?.blocks]);

  const updatePanelPosition = useCallback(() => {
    setPanelStyle(computePanelStyle(rootRef.current?.getBoundingClientRect()));
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    setPanelStyle(null);
  }, []);

  const togglePanel = useCallback(() => {
    setOpen((current) => {
      if (current) {
        setPanelStyle(null);
        return false;
      }
      setPanelStyle(computePanelStyle(rootRef.current?.getBoundingClientRect()));
      return true;
    });
  }, []);

  const handleOpen = useCallback(() => {
    if (useClick) return;
    updatePanelPosition();
    setOpen(true);
  }, [updatePanelPosition, useClick]);

  const handleClose = useCallback(() => {
    if (useClick) return;
    closePanel();
  }, [closePanel, useClick]);

  useEffect(() => {
    if (!open) return undefined;
    const onReposition = () => updatePanelPosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open || !useClick) return undefined;
    const onPointerDown = (event) => {
      const panel = document.getElementById(popoverId);
      if (rootRef.current?.contains(event.target)) return;
      if (panel?.contains(event.target)) return;
      closePanel();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") closePanel();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [closePanel, open, popoverId, useClick]);

  if (!diff?.blocks?.length) return null;

  const popover =
    open && panelStyle ? (
      <div
        id={popoverId}
        className="schedule-activity-log__ecomm-popover gvisit-comm-suggestion__panel"
        style={panelStyle}
        role="dialog"
        aria-label="Template details"
        onMouseEnter={useClick ? undefined : handleOpen}
        onMouseLeave={useClick ? undefined : handleClose}
      >
        <p className="gvisit-comm-suggestion__intro">
          {diff.hasOld ? (
            <>What <strong>changed</strong> in this template.</>
          ) : (
            <>What was <strong>saved</strong> in this template.</>
          )}
        </p>
        <NotificationsSection
          emailBlock={sections.emailBlock}
          smsBlock={sections.smsBlock}
          hasOld={diff.hasOld}
        />
        {sections.contentBlocks.map((block) => (
          <ContentBlock key={block.label} block={block} hasOld={diff.hasOld} />
        ))}
      </div>
    ) : null;

  return (
    <span
      ref={rootRef}
      className={`schedule-activity-log__ecomm-view${open ? " schedule-activity-log__ecomm-view--open" : ""}`}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
    >
      <button
        type="button"
        className="schedule-activity-log__view-link"
        aria-expanded={open}
        onClick={useClick ? togglePanel : undefined}
      >
        View
      </button>
      {typeof document !== "undefined" && popover ? createPortal(popover, document.body) : null}
    </span>
  );
}
